import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { classifyAssessment } from "@/lib/assessmentLogic";

/*
  ===========================================================================
  Storage & security stance
  ---------------------------------------------------------------------------
  This prototype intentionally avoids collecting any participant identifiers,
  addresses, medical histories, identifying incident details, or worker
  private information. The assessment is held in browser localStorage so
  that providers can pause and resume a consultation in one sitting.

  We use localStorage (rather than httpOnly cookies) because:
    1. The product spec mandates "frontend-only / local state only".
    2. There is no server endpoint to back a cookie with.
    3. The data persisted here is, by design, non-PII multiple-choice
       responses plus optional consultant notes (which the UI explicitly
       warns must not contain identifying information).

  We still defend in depth by:
    - Resetting state cleanly on "Start new assessment".
    - Catching and logging any storage failure (private browsing, quotas).
    - Versioning the storage key so structural migrations stay safe.
  ===========================================================================
*/

// Storage keys are versioned so structural migrations stay safe.
// v3 is the current write target (introduced with QC-01/02/03/04/05/08).
// v2 remains readable so pre-QC assessments still hydrate on resume — the v2
// blob is never overwritten or deleted; when a v3 write occurs it lives in
// its own key beside the historical v2 record.
const STORAGE_KEY = "niss0138_assessment_v3";
const LEGACY_STORAGE_KEY_V2 = "niss0138_assessment_v2";

const AssessmentContext = createContext(null);

const EMPTY_STATE = {
  answers: {},
  completedAt: null,
  assessmentId: null,
  consultantNotes: "",
  readinessActions: [],
  serverIntakeId: null,
  serverSavedAt: null,
  providerIntakeCompletedAt: null
};

function loadInitial() {
  if (typeof window === "undefined") return { ...EMPTY_STATE };
  try {
    // Prefer v3 if present; fall back to v2 read-only hydration so
    // historical assessments continue to open. v2 is never rewritten or
    // cleared here — the next persist cycle writes to v3 alongside it.
    let raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = window.localStorage.getItem(LEGACY_STORAGE_KEY_V2);
    }
    if (!raw) return { ...EMPTY_STATE };
    const parsed = JSON.parse(raw);
    return { ...EMPTY_STATE, ...parsed };
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("AssessmentContext: failed to read local state, starting fresh.", error);
    }
    return { ...EMPTY_STATE };
  }
}

function newReference() {
  const d = new Date();
  const ts = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(
    d.getDate()
  ).padStart(2, "0")}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `RDY-${ts}-${rand}`;
}

export function AssessmentProvider({ children }) {
  const [state, setState] = useState(loadInitial);
  // Phase 8C.1 — timestamp of the most recent successful localStorage write.
  // Distinct from state.serverSavedAt (which reflects opt-in server save).
  const [lastLocalSavedAt, setLastLocalSavedAt] = useState(() => {
    // Consider an existing saved blob (v3 or legacy v2) as "saved locally" at load time.
    try {
      const existing =
        window.localStorage.getItem(STORAGE_KEY) ||
        window.localStorage.getItem(LEGACY_STORAGE_KEY_V2);
      return existing ? new Date().toISOString() : null;
    } catch {
      return null;
    }
  });
  const [localSaveError, setLocalSaveError] = useState(false);

  // Persist to localStorage on every change. Wrapped in try/catch so quota
  // errors in private browsing or older browsers don't crash the app.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setLastLocalSavedAt(new Date().toISOString());
      setLocalSaveError(false);
    } catch (error) {
      setLocalSaveError(true);
      if (process.env.NODE_ENV !== "production") {
        console.warn("AssessmentContext: failed to persist local state.", error);
      }
    }
  }, [state]);

  // --- Actions (stable identities thanks to setState callback form) ---
  const setAnswer = useCallback((id, value) => {
    setState((prev) => ({
      ...prev,
      answers: { ...prev.answers, [id]: value }
    }));
  }, []);

  const completeAssessment = useCallback(() => {
    setState((prev) => ({
      ...prev,
      completedAt: new Date().toISOString(),
      assessmentId: prev.assessmentId || newReference()
    }));
  }, []);

  // Phase 8B — records that the Provider Intake stage (Sections 01-04) is
  // complete. Does NOT complete the assessment — that only happens at the end
  // of the Audit Readiness Questionnaire stage. Idempotent.
  const markProviderIntakeComplete = useCallback(() => {
    setState((prev) => ({
      ...prev,
      providerIntakeCompletedAt: prev.providerIntakeCompletedAt || new Date().toISOString(),
      assessmentId: prev.assessmentId || newReference()
    }));
  }, []);

  const resetAssessment = useCallback(() => {
    setState({ ...EMPTY_STATE });
  }, []);

  const setConsultantNotes = useCallback((value) => {
    setState((prev) => ({ ...prev, consultantNotes: value }));
  }, []);

  // Readiness Actions are consultant-controlled follow-up actions (Design
  // Spec 001 Phase 3). They record improvement/clarification steps only and
  // never feed scoring, classification, or source verification.
  const addReadinessAction = useCallback((text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    setState((prev) => ({
      ...prev,
      readinessActions: [
        ...(prev.readinessActions || []),
        { id: `ra-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: trimmed }
      ]
    }));
  }, []);

  const removeReadinessAction = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      readinessActions: (prev.readinessActions || []).filter((a) => a.id !== id)
    }));
  }, []);

  // Stage 4: records a successful optional server save. Display-only —
  // never feeds scoring, classification, or source verification.
  const markServerSaved = useCallback((id) => {
    setState((prev) => ({ ...prev, serverIntakeId: id, serverSavedAt: new Date().toISOString() }));
  }, []);

  // Memoise the derived classification so identity is stable per state change.
  const result = useMemo(() => {
    if (!state.completedAt) return null;
    return classifyAssessment(state.answers);
  }, [state.answers, state.completedAt]);

  // Bundle actions separately so they don't churn the context value.
  const actions = useMemo(
    () => ({
      setAnswer,
      completeAssessment,
      markProviderIntakeComplete,
      resetAssessment,
      setConsultantNotes,
      addReadinessAction,
      removeReadinessAction,
      markServerSaved
    }),
    [setAnswer, completeAssessment, markProviderIntakeComplete, resetAssessment, setConsultantNotes, addReadinessAction, removeReadinessAction, markServerSaved]
  );

  const value = useMemo(
    () => ({
      answers: state.answers,
      assessmentId: state.assessmentId,
      completedAt: state.completedAt,
      providerIntakeCompletedAt: state.providerIntakeCompletedAt || null,
      consultantNotes: state.consultantNotes || "",
      readinessActions: state.readinessActions || [],
      serverIntakeId: state.serverIntakeId || null,
      serverSavedAt: state.serverSavedAt || null,
      lastLocalSavedAt,
      localSaveError,
      result,
      ...actions
    }),
    [state.answers, state.assessmentId, state.completedAt, state.providerIntakeCompletedAt, state.consultantNotes, state.readinessActions, state.serverIntakeId, state.serverSavedAt, lastLocalSavedAt, localSaveError, result, actions]
  );

  return <AssessmentContext.Provider value={value}>{children}</AssessmentContext.Provider>;
}

export function useAssessment() {
  const ctx = useContext(AssessmentContext);
  if (!ctx) throw new Error("useAssessment must be used inside AssessmentProvider");
  return ctx;
}
